import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { ptBR } from 'date-fns/locale';
import { format, parseISO, isValid } from 'date-fns';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import { colorPrimary } from '../../theme';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { TUser } from '../../services';
import { UserRole } from '../../utils';

interface Data {
  id: number;
  name: string;
  data: string;
  lance: number;
}

interface ApiResponse {
  nuM_ID_REVENDA: number;
  nuM_ID_USER: number;
  revenda: string;
  daT_CADASTRO: string;
  namE_USER: string;
  vaL_PROPOSTA: number;
}

interface ColumnData {
  dataKey: keyof Data;
  label: string;
  numeric?: boolean;
  width?: number;
  icon?: any;
}



const columns: ColumnData[] = [
  { width: 0, label: 'Lances', dataKey: 'lance', numeric: true, icon:<PaidOutlinedIcon style={{color:colorPrimary}}/> },
  { width: 0, label: 'Nome', dataKey: 'name', icon:<DriveFileRenameOutlineIcon style={{color:colorPrimary}}/> },
  { width: 0, label: 'Data', dataKey: 'data', icon:<CalendarTodayIcon style={{color:colorPrimary}}/> },
];

const VirtuosoTableComponents: TableComponents<Data> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

function FixedHeaderContent(data: Data[], compreAgora:number | null) {

  const filteredData = compreAgora !== null ? data.filter((row) => row.lance !== compreAgora) : data;

  return (
    <>
    
      <TableRow>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          variant="head"
          style={{ width: column.width, textAlign: 'center', padding:"15px 0px" }}
          sx={{ backgroundColor: 'background.paper' }}
        >
        {column?.icon} 
        <b style={{ position: "relative", bottom: "5px", left:"5px" }}>
          {column.label === 'Lances' 
            ? `Lance${filteredData?.length > 1 ? 's' : ''} (${filteredData?.length})` 
            : column.label}
        </b>
        </TableCell>
      ))}
    </TableRow>
    {filteredData.length === 0 && (
        <TableRow>
        <TableCell colSpan={columns.length} align="center" style={{ padding: '10px', borderBottom: 'none' }}>
          Nenhum lance até o momento.
        </TableCell>
      </TableRow>
      
      )}
    </>
  );
}

function rowContent(_index: number, row: Data, isHovered: boolean, setIsHovered: React.Dispatch<React.SetStateAction<boolean>>, userId: string | null, compreAgora:number | null) {


  return (
<React.Fragment>
  {columns.map((column) => {
    if (compreAgora === row.lance && (column.dataKey === 'lance' || column.dataKey === 'name' || column.dataKey === 'data')) {
      return <TableCell key={column.dataKey} style={{ visibility: 'hidden', padding: "1px 1px" }}></TableCell>; 
    }

    return (
      <TableCell
        key={column.dataKey}
        align={column.numeric ? 'right' : 'left'}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          maxWidth: 150,
          background: isHovered ? '#f5f5f5' : 'transparent',
          padding: "15px 0px",
          textAlign: 'center'
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {column.dataKey === 'name' ? (
          row.id === Number(userId) ? (
            <span title={row.name} style={{ fontWeight: 'bold' }}>
              {row.name.length > 13 ? `${row.name.substring(0, 13)}...` : row.name}
            </span>
          ) : (
            <p style={{width:"100%", textAlign:"center"}}>Usuário</p>
          )
        ) : column.numeric ? (
          row[column.dataKey].toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        ) : column.dataKey === 'data' ? (
          isValid(parseISO(row.data)) ? format(parseISO(row.data), "dd/MM - HH:mm:ss", { locale: ptBR }) : row.data
        ) : (
          row[column.dataKey]
        )}
      </TableCell>
    );
  })}
</React.Fragment>




  );
}

interface PropsList {
  data: ApiResponse[];
  user: any;
  compreAgora:number | null
  cotacao?: boolean;
  
}

export default function ListTableLance({ data, user, compreAgora, cotacao }: PropsList) {
  const formattedData: Data[] = data.map((item) => ({
    id: item.nuM_ID_USER,
    name: item.namE_USER,
    data: item.daT_CADASTRO,
    lance: item.vaL_PROPOSTA,
  }));

  const [isHovered, setIsHovered] = React.useState(false);

  const [sid, setSid] = React.useState<string | null>(null);


  React.useEffect(() => {
    const storedSid = sessionStorage.getItem("sid");
    if (storedSid !== null && storedSid.trim() !== '') {
      setSid(storedSid);
    }
  }, []);


  return (
    <>
    
    {formattedData && formattedData.length > 0 ? (
    <Paper style={{ width: '100%', boxShadow: 'none' }}>

      <TableHead> 
            <TableRow>
              <TableCell align="center" colSpan={1}>
                {cotacao ? 'Propostas feitas' : <h2>Tabela de Lances</h2>}
              </TableCell>
             
            </TableRow>
            </TableHead>

      <TableVirtuoso
        data={formattedData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={() => FixedHeaderContent(formattedData, compreAgora)}
        itemContent={(index, row) => rowContent(index, row, isHovered, setIsHovered, sid, compreAgora)}
        style={{ boxShadow: 'none', color: 'transparent', maxHeight: '250px', position:"relative", bottom:"5px", height:"70%" }}
      />
      </Paper>
    ) : (
      <Paper style={{  textAlign: 'center', boxShadow: 'none' }}>
        <TableHead> 
            <TableRow>
              <TableCell colSpan={1}>
                {cotacao ? 'Propostas feitas' : <h2>Tabela de Lances</h2>}
              </TableCell>
             
            </TableRow>
            </TableHead>
        <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            style={{ width: 200, textAlign: 'center', position:"relative", bottom:"10px", padding:"15px 0px" }}
            sx={{ backgroundColor: 'background.paper' }}
          >
          {column?.icon} 
          <b style={{ position: "relative", bottom: "5px", left:"0px" }}>
            {column.label === 'Lances' 
              ? `Lance${data.length > 1 ? 's' : ''} (${data.length})` 
              : column.label}
          </b>
          </TableCell>
        ))}
      </TableRow>

        <p style={{padding:"15px 0px", position:"relative", bottom:"5px"}}> 
          {cotacao ? 'Nenhuma proposta até o momento.' : 'Nenhum lance até o momento.' }</p>
      </Paper>
    )}
    </>

    
  );
}
